import { fabric } from "fabric";
import { RectTablePropertiesEnum } from "../../../enums/RectTablePropertiesEnum";
import { CanvasHandler } from "../../../handlers";
import { v4 } from "uuid";
import { SeatFactory } from "../factories/seat.factory";
import { DirectionEnum, ObjectSuperTypesEnum } from "../../../enums";
import store from "../../../redux/store";
import { TablePropertiesBase } from "./TablePropertiesBase";
import { RectTableSeatHelper } from "../helpers/RectTableSeatHelper";

class RectTableProperties extends TablePropertiesBase {
  public handlers: any;
  private readonly SEAT_RADIUS = 7.5;
  private readonly SEAT_GAP = 8;
  public readonly ID = v4();
  
  constructor(canvasHandler: CanvasHandler) {
    super(canvasHandler)
    this.canvasHandler = canvasHandler;
    this.handlers = {
        [RectTablePropertiesEnum.SeatsQuantityUp]: this.updateSeatsUp,
        [RectTablePropertiesEnum.SeatsQuantityDown]: this.updateSeatsDown,
        [RectTablePropertiesEnum.SeatsQuantityLeft]: this.updateSeatsLeft,
        [RectTablePropertiesEnum.SeatsQuantityRight]: this.updateSeatsRight,
        [RectTablePropertiesEnum.TableHeight]: this.updateTableHeight,
        [RectTablePropertiesEnum.TableWidth]: this.updateTableWidth,
        [RectTablePropertiesEnum.SelectionType]: this.updateSelectionType,
        [RectTablePropertiesEnum.SectionLabel]: this.updateSectionLabel,
        [RectTablePropertiesEnum.TableLabelingLabel]: this.updateTableLabel,
        [RectTablePropertiesEnum.TableLabelingDisplayedLabel]: this.updateTableDisplayedLabel,
        [RectTablePropertiesEnum.TableLabelingVisible]: this.updateLabelVisibility,
        [RectTablePropertiesEnum.SeatLabelingType]: this.updateSeatLabelingDistribution,
        [RectTablePropertiesEnum.SeatLabelingStartsAt]: this.updateSeatLabelingStartsAt,
        [RectTablePropertiesEnum.Category]: this.updateCategory,
    };
  }

  public updateProperties = (
    selectedItem: any,
    key: string, 
    value: any, 
    allValues: any, 
    ) => {
        const handler = this.handlers[key];
        const state = store.getState();
        const { properties: { categories } } = state;
        const tableGroup = selectedItem.getObjects().find((obj: any) => obj.superType === ObjectSuperTypesEnum.Table);
        const table = tableGroup?.getObjects("rect")[0];
        const tableText = tableGroup?.getObjects("text")[0];
        const seats = selectedItem.getObjects().filter((obj: any) => obj.superType === ObjectSuperTypesEnum.Seat);
        const selectedCategory: any = categories.find((category: any) => category.key === value) ?? null;

        if (!handler) {
            console.warn(`No handler for property: ${key}`);
            return;
        }

        handler.call(this, {
            value,
            allValues,
            selectedItem,
            table,
            tableGroup,
            tableText,
            seats,
            selectedCategory
        });
        this.canvasHandler.canvas.requestRenderAll();
    };

    private updateSeatsUp = ({ value, selectedItem, table, tableGroup }: any) => {
        this.updateSeatsSide({ value, selectedItem, table, side: DirectionEnum.Up, tableGroup });
    };

    private updateSeatsDown = ({ value, selectedItem, table, tableGroup }: any) => {
        this.updateSeatsSide({ value, selectedItem, table, side: DirectionEnum.Down, tableGroup });
    };

    private updateSeatsLeft = ({ value, selectedItem, table, tableGroup }: any) => {
        this.updateSeatsSide({ value, selectedItem, table, side: DirectionEnum.Left, tableGroup });
    };

    private updateSeatsRight = ({ value, selectedItem, table, tableGroup }: any) => {
        this.updateSeatsSide({ value, selectedItem, table, side: DirectionEnum.Right, tableGroup });
    };

    private updateTableHeight = ({ value, selectedItem, table, tableGroup }: any) => {
        const seatDiameter = this.SEAT_RADIUS * 2;
        const { seatsQuantity: {right, left} } = selectedItem;
        if( value <= (right * seatDiameter) || value <= (left * seatDiameter) ) return;
        this.updateTableSize({ value, selectedItem, table, tableGroup, dimension: "height" });
    };

    private updateTableWidth = ({ value, selectedItem, table, tableGroup }: any) => {
        const seatDiameter = this.SEAT_RADIUS * 2;
        const { seatsQuantity: {up, down} } = selectedItem;
        if( value <= (up * seatDiameter) || value <= (down * seatDiameter) ) return;
        this.updateTableSize({ value, selectedItem, table, tableGroup, dimension: "width" });
    };

    private updateTableSize = ({ value, selectedItem, table, tableGroup, dimension }: any) => {
        if (!selectedItem) return;

        const prevCenter = selectedItem.getCenterPoint();

        table.set({
            [dimension]: value
        });
        
        table.setCoords();

        tableGroup.addWithUpdate()
        
        const maxSeats = RectTableSeatHelper.calculateMaxSeats({table, seatRadius: this.SEAT_RADIUS});
        
        selectedItem.set({maxSeats});
        
        RectTableSeatHelper.repositionExistingSeats({
            rectTable: selectedItem, table, tableGroup, seatRadius: this.SEAT_RADIUS, seatGap: this.SEAT_GAP
        });
        
        selectedItem.addWithUpdate();
        selectedItem.setPositionByOrigin(prevCenter, "center", "center");
        selectedItem.setCoords();
    };

    private updateSeatsSide = async ({value, selectedItem, table, side, tableGroup}: any) => {
        await RectTableSeatHelper.rebuildSeats({
            rectTable: selectedItem,
            table,
            tableGroup,
            newQuantity: value,
            direction: side,
            seatRadius: this.SEAT_RADIUS,
            seatGap: this.SEAT_GAP,
            setSeatValue: this.setSeatValue,
        });
    };

    protected getOrderedTableSeats = ({ seats, table }: any) => {
        return RectTableSeatHelper.getOrderedTableSeats({ seats, table });
    };
}
    

export default RectTableProperties;